import T from 'T'
import round from 'lodash/round'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'

import { createSelector } from 'reselect'
import { get } from 'utils/lodash'
import { getOrdinal } from 'utils/day'
import { getPricingPeriod, isPriceTypeDollar } from 'utils/service'
import { getFirstMonthsScheduleOptions, getQuarterlyScheduleOptions } from 'utils/billingSchedule'
import { BILLING_CYCLES, PAYMENT_DUE_DATE_TYPES } from 'settings/constants/billing'
import { isString } from 'utils/string'
import { PAYMENT_TERMS } from 'components/customer-details/content/payment-terms/settings'

const { QUARTER } = BILLING_CYCLES
const { DUE_ON_RECEIPT } = PAYMENT_TERMS

const DEFAULT_ROUND = 2
const DEFAULT_STRING_VALUE = '-'
const DEFAULT_NUMBER_VALUE = 0
const billingProfileKey = 'accountBilling.billingProfile'
const LAST_DAY_TEXT = 'Last Day of Month'

// Empty string is a valid case
export const isTaxExempt = taxExempt => isString(taxExempt) || taxExempt
export const isDueOnReceipt = term => term === DUE_ON_RECEIPT
export const getPaymentTermsOptionText = (term, interval) => (isDueOnReceipt(term) ? term : `${term} ${interval}`).trim()
export const getSortedPaymentTerms = paymentTerms => orderBy(paymentTerms, ['term', 'interval'], ['asc', 'asc'])

const selectAccountName = customer => get(customer, 'accountName', '')
const selectProfileName = customer => get(customer, `${billingProfileKey}.profile`, 'Custom')
const selectParentBillingProfileId = customer => get(customer, `${billingProfileKey}.parentBillingProfileId`, '')
const selectCycleInterval = customer => get(customer, `${billingProfileKey}.billingCycle.interval`)
const selectCycleIntervalType = customer => get(customer, `${billingProfileKey}.billingCycle.intervalType`)
const selectNextBillingDate = customer => get(customer, 'accountBilling.nextBillingDate', DEFAULT_STRING_VALUE)
const selectPaymentType = customer => get(customer, `${billingProfileKey}.paymentType`, DEFAULT_STRING_VALUE)
const selectTiming = customer => get(customer, `${billingProfileKey}.timing`, DEFAULT_STRING_VALUE)
const selectTimingDay = customer => get(customer, `${billingProfileKey}.timingDay`, '')
const selectTimingWeek = customer => get(customer, `${billingProfileKey}.timingWeek`, '')
const selectTimingQuarter = customer => get(customer, `${billingProfileKey}.timingQuarter`, '')
const selectBillingMonths = customer => get(customer, `${billingProfileKey}.billingMonths`, '')
export const selectBalance = customer => get(customer, 'balanceDetails.balanceCents', DEFAULT_NUMBER_VALUE)
const selectCreditLimit = customer => get(customer, `${billingProfileKey}.creditLimit`, DEFAULT_NUMBER_VALUE)
const selectAvailableCredit = customer => get(customer, 'availableCredit', DEFAULT_NUMBER_VALUE)
const selectLateFeeType = customer => get(customer, `${billingProfileKey}.lateFeeType`, DEFAULT_NUMBER_VALUE)
const selectLateFeeAmount = customer => get(customer, `${billingProfileKey}.lateFeeAmount`, DEFAULT_NUMBER_VALUE)
const selectLateFeeThreshold = customer => get(customer, `${billingProfileKey}.lateFeeThreshold`, DEFAULT_NUMBER_VALUE)
const selectLateFeeGracePeriod = customer => get(customer, `${billingProfileKey}.lateFeeGracePeriod`, DEFAULT_NUMBER_VALUE)

export const selectPaymentTermsId = customer => {
  const key = isEmpty(customer?.paymentTerms) ? `${billingProfileKey}.paymentTerm.id` : 'paymentTerms.id'
  return get(customer, key, DEFAULT_STRING_VALUE)
}
const selectPaymentTerms = customer => {
  const key = isEmpty(customer?.paymentTerms) ? `${billingProfileKey}.paymentTerm` : 'paymentTerms'
  const term = get(customer, `${key}.term`, DEFAULT_STRING_VALUE)
  const interval = get(customer, `${key}.interval`, DEFAULT_NUMBER_VALUE)
  return getPaymentTermsOptionText(term, interval)
}
export const selectPaymentDueDateDisplay = customer => {
  const paymentDueDateType = get(customer, 'paymentDueDateType', PAYMENT_DUE_DATE_TYPES.NET_DAYS)
  if (paymentDueDateType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH) {
    const day = get(customer, 'paymentDayOfMonth')
    return day ? `Due on ${day}${getOrdinal(day)} of month` : DEFAULT_STRING_VALUE
  }
  return selectPaymentTerms(customer)
}

const selectDeliveryMethod = customer => get(customer, `${billingProfileKey}.deliveryMethod`, '')
const selectTaxExempt = customer => customer?.accountBilling?.taxExempt

const selectAccountBalance = account => get(account, 'balance', DEFAULT_NUMBER_VALUE)
const selectAccountCreditLimit = account => get(account, 'creditLimit', DEFAULT_NUMBER_VALUE)

export const getAvailableCredit = createSelector(
  [selectAccountBalance, selectAccountCreditLimit, state => state?.isRoundingOn],
  (balance, creditLimit, isRoundingOn) => {
    const availableCredit = Math.max(0, creditLimit - balance)
    // We are using <NumericFormat /> which has inbuilt rounding prop, In most of the cases this is not required
    return isRoundingOn ? round(availableCredit, DEFAULT_ROUND) : availableCredit
  }
)

export const getFirstMonths = createSelector(
  [state => state?.cycleInterval, state => state?.cycleIntervalType, state => state?.timingQuarter, state => state?.billingMonths],
  (cycleInterval, cycleIntervalType, timingQuarter, billingMonths) => {
    if (cycleIntervalType === QUARTER) {
      const quarterlyScheduleOptions = getQuarterlyScheduleOptions({ intervalType: cycleIntervalType, interval: cycleInterval })
      return get(
        quarterlyScheduleOptions.find(option => option.key === timingQuarter),
        'value',
        ''
      )
    }

    // Month, Year options
    const firstMonthsOptions = getFirstMonthsScheduleOptions({ intervalType: cycleIntervalType, interval: cycleInterval })
    if (firstMonthsOptions.length > 0) {
      return get(
        firstMonthsOptions.find(option => option.key === billingMonths),
        'value',
        ''
      )
    }

    return ''
  }
)

export const getTiming = createSelector(
  [state => state?.timing, state => state?.timingDay, state => state?.timingWeek, state => state?.cycleIntervalType],
  (timing, day, week, cycleIntervalType) => {
    // Check for cycle
    if (cycleIntervalType === T.MANUAL) {
      return DEFAULT_STRING_VALUE
    }
    if (typeof cycleIntervalType === 'string' && cycleIntervalType.includes(T.YEAR)) {
      return timing === T.ANNIVERSARY_LABEL ? T.ANNIVERSARY_VALUE : DEFAULT_STRING_VALUE
    }

    // Check for timing
    if (timing === T.ANNIVERSARY_LABEL) {
      return T.ANNIVERSARY_VALUE
    }
    if (timing === T.DAY && day) {
      return day === T.LAST ? LAST_DAY_TEXT : day + getOrdinal(day)
    }
    if (timing === T.DAY_OF_WEEK && week) {
      return week
    }
    return DEFAULT_STRING_VALUE
  }
)

export const getCustomerBillingDetails = createSelector(
  [
    selectAccountName,
    selectProfileName,
    selectParentBillingProfileId,
    selectCycleInterval,
    selectCycleIntervalType,
    selectNextBillingDate,
    selectPaymentType,
    selectTiming,
    selectTimingDay,
    selectTimingWeek,
    selectTimingQuarter,
    selectBillingMonths,
    selectBalance,
    selectCreditLimit,
    selectAvailableCredit,
    selectLateFeeType,
    selectLateFeeAmount,
    selectLateFeeThreshold,
    selectLateFeeGracePeriod,
    selectPaymentDueDateDisplay,
    selectDeliveryMethod,
    selectTaxExempt,
  ],
  (
    accountName,
    profileName,
    parentBillingProfileId,
    cycleInterval,
    cycleIntervalType,
    nextBillingDate,
    paymentType,
    timing,
    timingDay,
    timingWeek,
    timingQuarter,
    billingMonths,
    balance,
    creditLimit,
    availableCredit,
    lateFeeType,
    lateFeeAmount,
    lateFeeThreshold,
    lateFeeGracePeriod,
    paymentTermsDisplay,
    deliveryMethod,
    taxExempt
  ) => ({
    profileName: parentBillingProfileId ? `${profileName} (${accountName})` : profileName,
    parentBillingProfileId,
    billingCycle: getPricingPeriod(cycleInterval, cycleIntervalType),
    nextBillingDate,
    paymentType,
    timing: getTiming({ timing, timingDay, timingWeek, cycleIntervalType }),
    firstMonths: getFirstMonths({ cycleInterval, cycleIntervalType, timingQuarter, billingMonths }),
    balance,
    creditLimit,
    availableCredit,
    creditLeft: getAvailableCredit({ balance, creditLimit }),
    isLateFeeTypeDollar: isPriceTypeDollar(lateFeeType),
    lateFeeAmount,
    lateFeeThreshold,
    lateFeeGracePeriod: `${lateFeeGracePeriod} day${lateFeeGracePeriod === 1 ? '' : 's'}`,
    paymentTerms: paymentTermsDisplay,
    deliveryMethod: isString(deliveryMethod) ? deliveryMethod.replaceAll(',', ', ') : '',
    taxExempt: isTaxExempt(taxExempt) ? 'Exempt' : 'Not Exempt',
  })
)
